import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

import Passthrough from "@/views/Passthrough";

import Login from "@/views/TwoStepLogin";
import User from "@/views/User";
import Controller from "@/views/Controller";
import Token from "@/views/Token";
import Autologin from "@/views/Autologin";
// Main user views
import Dashboard from "@/views/user/Dashboard";
import Profile from "@/views/user/Profile";
import Account from "@/views/user/Account";
import Awareness from "@/views/user/Awareness";
import Security from "@/views/user/Security";
import Impact from "@/views/user/Impact";
import Wellbeing from "@/views/user/Wellbeing";
import Archetype from "../views/user/Archetype.vue";
import SocialEngineering from "@/views/user/SocialEngineering";
import Training from "@/views/user/Training";
import TrainingDetail from "@/views/user/TrainingDetail";
import Breaches from "@/views/user/Breaches";
import Faq from "@/views/user/Faq";
import Assets from "@/views/user/Assets";

// Main controller views
import ControllerDashboard from "@/views/controller/Dashboard";
import Organization from "@/views/controller/Organization";
import ActionPlan from "@/views/controller/ActionPlan";
import Campaigns from "@/views/controller/phishing/Campaigns";
import CreateCampaign from "@/views/controller/phishing/CreateCampaign";
import ViewCampaign from "@/views/controller/phishing/ViewCampaign";
import Users from "@/views/controller/Users";
import UserDetail from "@/views/controller/UserDetail";
import Departments from "@/views/controller/Departments";
import DepartmentDetail from "@/views/controller/DepartmentDetail";

// Aux views
import Cybertest from "@/views/user/Cybertest";

// Generic views
import NotFound from "@/views/NotFound";
import FederationNotFound from "@/views/FederationNotFound";
import Maintenance from "@/views/Maintenance";

import VueScrollTo from "vue-scrollto";

Vue.use(VueRouter);

const ifNotAuthenticated = (to, from, next) => {
  VueScrollTo.scrollTo("body");
  if (!store.getters["Auth/isLoggedIn"]) {
    next();
    return;
  }
  next({ name: "Dashboard" });
};

const ifAuthenticated = (to, from, next) => {
  VueScrollTo.scrollTo("body");
  if (
    store.getters["Auth/isLoggedIn"] &&
    to.meta.profile.includes(localStorage.getItem("profile")) &&
    store.getters["Auth/hasModule"](to.meta.modules)
  ) {
    next();
    return;
  }
  store.dispatch("Auth/logout");
};

const routes = [
  {
    path: "/",
    name: "Login",
    component: Login,
    beforeEnter: ifNotAuthenticated
  },
  {
    path: "/user",
    name: "User",
    component: User,
    children: [
      {
        name: "Dashboard",
        path: "dashboard",
        component: Dashboard,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Profile",
        path: "profile",
        component: Profile,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Awareness",
        path: "profile/awareness",
        component: Awareness,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["awareness"],
          bot: true
        }
      },
      {
        name: "Wellbeing",
        path: "profile/wellbeing",
        component: Wellbeing,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["wellbeing"],
          bot: true
        }
      },
      {
        name: "Archetype",
        path: "profile/archetype",
        component: Archetype,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["wellbeing"],
          bot: true
        }
      },
      {
        name: "Security",
        path: "profile/security",
        component: Security,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["risk"],
          bot: true
        }
      },
      {
        name: "Impact",
        path: "profile/impact",
        component: Impact,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["risk"],
          bot: true
        }
      },
      {
        name: "SocialEngineering",
        path: "profile/social-engineering",
        component: SocialEngineering,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["risk"],
          bot: true
        }
      },
      {
        name: "Training",
        path: "sessions/:typeId?",
        component: Training,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "TrainingDetail",
        path: "sessions/detail/:id",
        component: TrainingDetail,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Faq",
        path: "faq/:section?",
        component: Faq,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Assets",
        path: "assets/:status?/:family?/:priority?",
        component: Assets,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Cybertest",
        path: "cybertest/:surveyEntityId",
        component: Cybertest,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          nomenu: true
        }
      },
      {
        name: "Account",
        path: "account/:option?",
        component: Account,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          bot: true
        }
      },
      {
        name: "Breaches",
        path: "breaches",
        component: Breaches,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER", "USER"],
          modules: ["gaming"]
        }
      }
    ],
    beforeEnter: ifAuthenticated,
    meta: {
      profile: ["CONTROLLER", "USER"]
    }
  },
  {
    path: "/controller",
    name: "Controller",
    component: Controller,
    children: [
      {
        name: "ControllerDashboard",
        path: "dashboard",
        component: ControllerDashboard,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        path: "campaigns",
        component: Passthrough,
        meta: {
          modules: ["phishing"]
        },
        children: [
          {
            name: "ListCampaigns",
            path: "",
            component: Campaigns,
            beforeEnter: ifAuthenticated,
            meta: {
              profile: ["CONTROLLER"]
            }
          },
          {
            name: "CreateCampaign",
            path: "new",
            component: CreateCampaign,
            beforeEnter: ifAuthenticated,
            meta: {
              profile: ["CONTROLLER"]
            }
          },
          {
            name: "ViewCampaign",
            path: ":id",
            component: ViewCampaign,
            beforeEnter: ifAuthenticated,
            meta: {
              profile: ["CONTROLLER"]
            }
          }
        ]
      },
      {
        name: "Users",
        path: "users",
        component: Users,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        name: "UserDetail",
        path: "users/:id",
        component: UserDetail,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        name: "Departments",
        path: "departments",
        component: Departments,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        name: "DepartmentDetail",
        path: "departments/:id",
        component: DepartmentDetail,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        name: "Organization",
        path: "organization",
        component: Organization,
        beforeEnter: ifAuthenticated,
        meta: {
          profile: ["CONTROLLER"]
        }
      },
      {
        name: "ActionPlan",
        path: "action-plan",
        component: ActionPlan,
        beforeEnter: ifAuthenticated,
        meta: {
          modules: ["risk"],
          profile: ["CONTROLLER"]
        }
      }
    ]
  },
  {
    name: "Token",
    path: "/password/:token",
    component: Token,
    beforeEnter: ifNotAuthenticated
  },
  {
    name: "Autologin",
    path: "/token/",
    component: Autologin
  },
  {
    name: "Maintenance",
    path: "/maintenance",
    component: Maintenance
  },
  {
    // will match everything
    path: "/federation/not-found",
    name: "FederationNotFound",
    component: FederationNotFound
  },
  {
    // will match everything
    path: "/company/action-plan",
    name: "CompanyActionPlan",
    beforeEnter: ifAuthenticated,
    meta: {
      profile: ["CONTROLLER"]
    },
    component: ActionPlan
  },
  {
    // will match everything
    path: "*",
    name: "NotFound",
    component: NotFound
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

export default router;
